import Reflux from 'reflux';
import BaseMixin from 'flux/stores/BaseMixin';
import { Map, OrderedMap, List, fromJS } from 'immutable';
import NewGlobalElementModalActions from 'flux/actions/NewGlobalElementModalActions';
import ModalActions from 'flux/actions/ModalActions';
import GlobalElementActions from 'flux/actions/GlobalElementActions';
import GlobalElementStore from 'flux/stores/GlobalElementStore';
import CuesheetStore from 'flux/stores/cuesheet/CuesheetStore';
import UIRegions from 'shared/constants/UIRegions';
import ToastActions from 'flux/actions/ToastActions';

/**
 *  New Global Element Modal Store
 *
 * This store is for the modal that allows you to create a new
 * global element from a Cue Row
 */
export default Reflux.createStore({
  listenables: [NewGlobalElementModalActions],

  mixins: [BaseMixin],

  init() {
    this._init();
    this.listenTo(GlobalElementStore, 'onGlobalElementStoreChange');
    this.listenTo(CuesheetStore, 'onCuesheetStoreChange');
  },

  _init() {
    this.setStateAndTrigger(
      Map({
        activeElementSet: OrderedMap(),
        elementSets: OrderedMap(),
        activeTags: List(),
        activeRows: List(),

        //Used to determine if there is a mistach in the number of columns between
        //the current show in cuesheet and the global element manager.
        columnDiscrepancy: false,
      }),
    );
  },

  onCuesheetStoreChange(state) {
    let next = this.getState()
      .set('eventId', state.get('eventId'))
      .set('showId', state.get('showId'))
      .set('itemSetListId', state.get('itemSetListId'))
      .set('selectedCells', state.get('selectedCells'))
      .set('rows', state.get('rows'))
      .set('fullColumnOrder', state.get('fullColumnOrder'))
      .set('rowMap', state.get('rowMap'));

    next = this._checkColumnsDiscrepancy(next);

    this.setStateAndTrigger(next);
  },

  onGlobalElementStoreChange(state) {
    const next = this.getState()
      .set('geTeamId', state.get('teamId'))
      .set('geEventId', state.get('eventId'))
      .set('geShowId', state.get('showId'))
      .set('geItemSetListId', state.get('itemSetListId'))
      .set('geItemSetId', state.get('itemSetId'))
      .set('elementSets', state.get('elementSets') || OrderedMap())
      .set('tags', state.get('tags'));

    this.setStateAndTrigger(next);
  },

  onPopulateSelection() {
    let selectedRowIndices = [];

    this.getInState('rows').forEach((row, rowIndex) => {
      if (row.get('rowSelected')) selectedRowIndices.push(rowIndex);
    });

    NewGlobalElementModalActions.populate(selectedRowIndices);
  },

  onPopulate(rowIndices) {
    ModalActions.show(UIRegions.NewGlobalElementModal);

    let next = this.getState();

    rowIndices = rowIndices || [this.getSelectedCellRowIndex()];

    next = next.set('activeRows', List());

    rowIndices.forEach((rowIndex) => {
      if (rowIndex == -1) return;

      const rowPath = this.getInState(['rows', rowIndex, 'path']),
        itemSetId = rowPath[0],
        rowId = rowPath[2];
      // item = this.getInState(['rowMap'].concat(rowPath));

      next = next.update('activeRows', (rows) => {
        return rows.push(
          Map({
            event: next.get('eventId'),
            show: next.get('showId'),
            itemSetList: next.get('itemSetListId'),
            itemSet: itemSetId,
            row: rowId,
          }),
        );
      });
    });

    this.setStateAndTrigger(next);
  },

  onPopulateItemSetCompleted(itemSetList) {
    let next = this.getState();

    const geColumnOrder = fromJS(itemSetList.defaultColumnOrder);

    next = next.set('activeElementSetDefaultColumnOrder', geColumnOrder);
    next = this._checkColumnsDiscrepancy(next);

    this.setStateAndTrigger(next);
  },

  /*
   * Check if there is a mismatch between the number of columns in the current
   * show (fullColumnOrder) and global element manager element set
   * (activeElementSetDefaultColumnOrder). If there is a mistach between
   * columns, alert the user so that they are aware that the extra columns data may
   * not appear when they create or publish changes to the GE Manager from the
   * current show.
   *
   * See: https://github.com/SHOFLO/Issues/issues/4099
   */
  _checkColumnsDiscrepancy(next) {
    const columnOrder = next.get('fullColumnOrder');
    const geColumnOrder = next.get('activeElementSetDefaultColumnOrder');

    if (!columnOrder || !geColumnOrder) return next;

    const columnDiscrepancy = columnOrder.size !== geColumnOrder.size;

    return next.set('columnDiscrepancy', columnDiscrepancy);
  },

  getSelectedCellRowIndex() {
    let state = this.getState();

    if (state.get('selectedCells').size < 1) return -1;

    let { rowId } = state.get('selectedCells').last(),
      itemSetId = state.get('rowMap').keySeq().first();

    return this.findRowPosition(itemSetId, rowId);
  },

  /**
   * Get the row index, given an itemSetId and a rowId
   *
   * @param itemSetId
   * @param rowId
   */
  findRowPosition(itemSetId, rowId) {
    let state = this.getState();

    let rows = state.get('rows');

    if (!rows) return -1;

    return rows.findIndex(
      (rowSpec) =>
        rowSpec.get('path')[0] === itemSetId &&
        rowSpec.get('path')[2] === rowId,
    );
  },

  onChangeActiveElementSet(elementSetId) {
    let next = this.getState();

    next = next
      .set('activeTags', List())
      .set('activeElementSet', next.getIn(['elementSets', elementSetId]));

    this.setStateAndTrigger(next);

    GlobalElementActions.populateElementSet(
      this.getInState('geTeamId'),
      this.getInState('geEventId'),
      elementSetId,
    );

    NewGlobalElementModalActions.populateItemSet(
      this.getInState('geTeamId'),
      this.getInState('geEventId'),
      elementSetId,
    );
  },

  onChangeActiveTag(tagIds) {
    this.setInStateAndTrigger('activeTags', tagIds);
  },

  // eslint-disable-next-line no-unused-vars
  onPublish(teamId, eventId, showId) {
    const state = this.getState();

    if (
      state.get('activeTags').size < 1 ||
      !state.getIn(['activeElementSet', '_id'])
    )
      return;

    ModalActions.hide(UIRegions.NewGlobalElementModal);

    NewGlobalElementModalActions.createGlobalElementFromShowRow(
      state.get('geTeamId'),
      state.get('geEventId'),
      state.getIn(['activeElementSet', '_id']),
      state.get('geItemSetListId'),
      state.get('geItemSetId'),
      state.get('activeTags').toJS(),
      state.get('activeRows').toJS(),
    );
  },

  onCreateGlobalElementFromShowRowCompleted() {
    ToastActions.success('Success');
  },

  onClear() {
    const next = this.getState()
      .delete('activeItemSetId')
      .delete('activeRowId')
      .delete('activeTag');

    this.setStateAndTrigger(next);

    ModalActions.hide(UIRegions.NewGlobalElementModal);
  },
});
